import { useMutation, useQueryClient } from '@tanstack/react-query'
import { aciIntegrationsService } from 'v2/shared/services/aci-integrations-service'
import { externalAppsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RCustomMcpServer, RExternalAppDetails, RExternalOrgApp } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { generateToastsForMutation } from 'v2/utils/toasts'
import { type CustomMcpFormValues, toCustomMcpPayload } from './apps/custom-mcp-form.config'
import type { IntegrationFormValues } from './apps/integration-connection-form.config'
import { prepareConnectionDetails } from './apps/integration-form-utils'
import { invalidateExternalAppQueries } from './connection-mutations'
import type { OAuthProviderCredentials } from './oauth-provider-row.config'
import { getSafeIntegrationErrorMetadata } from './utils/safe-error-metadata'

type IntegrationConnectResult =
  | { kind: 'redirect'; url: string }
  | { kind: 'connected'; appName: string }

interface IntegrationConnectVariables {
  app: RExternalAppDetails
  values: IntegrationFormValues
}

export const useConnectIntegrationMutation = () => {
  const toastOptions = generateToastsForMutation<
    IntegrationConnectResult,
    IntegrationConnectVariables
  >(
    {
      title: 'Connected',
      description: 'The integration connected successfully.',
    },
    {
      title: 'Integration Error',
      description: 'Could not start authentication via the bridge.',
      callback: (error) =>
        logger.error('Integration connection failed', getSafeIntegrationErrorMetadata(error)),
    },
  )
  const notifySuccess = toastOptions.onSuccess as
    | ((result: IntegrationConnectResult) => void)
    | undefined

  return useMutation({
    mutationFn: async ({
      app,
      values,
    }: IntegrationConnectVariables): Promise<IntegrationConnectResult> => {
      const { connectionDetails, otherDetails } = prepareConnectionDetails(values, app)
      const connectionConfig = { ...connectionDetails.connection, ...otherDetails }
      const response = await aciIntegrationsService.connect(
        app.provider,
        `${window.location.origin}/integrations`,
        {
          connection_config: Object.keys(connectionConfig).length ? connectionConfig : undefined,
          api_key: connectionDetails.credentials.api_key
            ? String(connectionDetails.credentials.api_key)
            : undefined,
          username: connectionDetails.credentials.username
            ? String(connectionDetails.credentials.username)
            : undefined,
          password:
            connectionDetails.credentials.password === undefined
              ? undefined
              : String(connectionDetails.credentials.password),
        },
      )
      if (response.data.consent_url) {
        return { kind: 'redirect', url: response.data.consent_url }
      }
      if (response.data.linked_account) {
        return { kind: 'connected', appName: app.name }
      }
      throw new Error('The integrations service returned no actionable result')
    },
    ...toastOptions,
    // A redirect is not a success yet: the landing page toasts once the provider returns.
    onSuccess: (result) => {
      invalidateExternalAppQueries()
      if (result.kind === 'connected') notifySuccess?.(result)
    },
  })
}

export type RegisterCustomMcpVariables = CustomMcpFormValues & { icon?: string | null }

export const useRegisterCustomMcpMutation = () => {
  const client = useQueryClient()
  const toastOptions = generateToastsForMutation<RCustomMcpServer, RegisterCustomMcpVariables>(
    {
      title: 'MCP server connected',
      description: 'The MCP server and its tools are now available.',
      callback: () => invalidateExternalAppQueries(client),
    },
    {
      title: 'Connection failed',
      description:
        'Could not connect to the MCP server. Check the URL and credentials, then try again.',
      callback: (error) =>
        logger.error('Custom MCP registration failed', getSafeIntegrationErrorMetadata(error)),
    },
  )

  return useMutation({
    mutationFn: async ({ icon, ...values }: RegisterCustomMcpVariables) => {
      const response = await aciIntegrationsService.registerCustomMcp(
        toCustomMcpPayload(values, icon),
      )
      if (!response.data?.id) throw new Error('Custom MCP registration returned no server id')
      return response.data
    },
    ...toastOptions,
  })
}

export const useSetOAuthProviderCredentialsMutation = () => {
  const client = useQueryClient()
  const toastOptions = generateToastsForMutation<
    unknown,
    { provider: string; credentials: OAuthProviderCredentials }
  >(
    {
      title: 'Saved',
      description: 'OAuth provider credentials were saved.',
      callback: () =>
        client.invalidateQueries({ queryKey: externalAppsQueryKeys().oauthProvidersBase }),
    },
    { title: 'Error', description: 'Failed to save credentials.' },
  )
  return useMutation({
    mutationFn: ({
      provider,
      credentials,
    }: {
      provider: string
      credentials: OAuthProviderCredentials
    }) =>
      aciIntegrationsService.setOAuthCredentials(provider, {
        client_id: credentials.clientId,
        client_secret: credentials.clientSecret,
      }),
    ...toastOptions,
  })
}

export const useConfirmAciConnectionMutation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (ownerId: string): Promise<RExternalOrgApp | undefined> => {
      const response = await aciIntegrationsService.getConnections()
      return response.data.find((connection) => connection.id === ownerId)
    },
    // This runs on the OAuth landing page, the moment a brand-new connection
    // comes back from the provider. Without invalidation the user is told
    // "Connected" and then lands on an integrations list — and a flow's app
    // picker — that was cached before the connection existed.
    onSuccess: () => invalidateExternalAppQueries(client),
  })
}
